import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { AuthContextType, AuthError } from '../types';
import { logout as authLogout } from '../../../services/authService';
import { bootstrapAuth } from '../../../services/authService';
import { User } from '@/types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [shouldShowTour, setShouldShowTour] = useState(false);

  const updateUser = (newUser: User | null) => {
    setUser(newUser);
    
    if (newUser) {
      localStorage.setItem('cached_user', JSON.stringify(newUser));
    } else {
      localStorage.removeItem('cached_user');
    }
  };

  const dismissTour = () => {
    setShouldShowTour(false);
    if (user) {
      const tourCompletedKey = `tour_completed_${user.email}`;
      localStorage.setItem(tourCompletedKey, 'true');
    }
    localStorage.setItem('has_seen_tour', 'true');
  };

  const startTour = () => {
    if (user) {
      const tourCompletedKey = `tour_completed_${user.email}`;
      const hasCompletedTour = localStorage.getItem(tourCompletedKey) === 'true';
      
      if (!hasCompletedTour) {
        setShouldShowTour(true);
      }
    }
  };

  useEffect(() => {
    const cachedUser = localStorage.getItem('cached_user');
    if (cachedUser) {
      try {
        const parsedUser = JSON.parse(cachedUser);
        setUser(parsedUser);
      } catch (error) {
        localStorage.removeItem('cached_user');
      }
    }


    const initializeAuth = async () => {
      try {
        const { user: authUser } = await bootstrapAuth();
        setUser(authUser);
        
        if (authUser) {
          localStorage.setItem('cached_user', JSON.stringify(authUser));
        } else {
          localStorage.removeItem('cached_user');
        }
      } catch (error) {
        setUser(null);
        localStorage.removeItem('cached_user');
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  useEffect(() => {
    if (!user || loading) return;

    const shouldStartTour = localStorage.getItem('start_tour_on_load');
    
    if (shouldStartTour === 'true') {
      localStorage.removeItem('start_tour_on_load');
      
      const tourCompletedKey = `tour_completed_${user.email}`;
      const hasCompletedTour = localStorage.getItem(tourCompletedKey) === 'true';
      
      if (!hasCompletedTour) {
        setTimeout(() => {
          setShouldShowTour(true);
        }, 1000);
      }
    }
  }, [user, loading]);

  const logout = async (): Promise<void> => {
    try {
      await authLogout();
      setUser(null);
    } catch (error) {
      throw error as AuthError;
    }
  };



  const value: AuthContextType = {
    user,
    loading,
    logout,
    updateUser,
    shouldShowTour,
    dismissTour,
    startTour,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 